import { useCallback, useRef } from 'react';

import {
    sanitizeItemForSave,
    useCreateOrUpdateItem,
    useRemoteValidation,
} from '@/dialogs/item-editor/ItemEditor.queries';
import { useItemChangesQueue } from '@/hooks/useItemChangesQueue';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getInjector } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { PersistedItem } from '@/types/DataModel';
import {
    RealTimeMessagingActions,
    RealTimeMessagingEvents,
} from '@/types/RealTimeMessagingEvents';

// Legacy debounce intervals from items-controller.js lines 786-789.
const TRANSMIT_DEBOUNCE_MS = 300;
const VALIDATE_DEBOUNCE_MS = 600;
const STORE_DEBOUNCE_MS = 2000;

interface ValidateSuccessArgs {
    primaryKey: string;
    /** The response from POST /validate. */
    response: unknown;
    /** The attribute names that were included in the validation request. */
    attributeNames: string[] | null;
    /** Item snapshot that was validated. */
    processingItem: PersistedItem;
}

interface SaveSuccessArgs {
    /** The saved item as returned by POST /items. */
    savedItem: PersistedItem;
    /**
     * The patch that was sent in this save. Contains the attribute
     * name -> value pairs the user edited since the last successful
     * save (values may be `null` for cleared fields).
     *
     * Callers merge this into the React Query cache alongside
     * `savedItem` because the server's JSON response omits null-valued
     * attributes (see `FromItemConverter.setProperty`): without the
     * patch, unchecking a `SingleBoolean` would save correctly on the
     * server but leave the stale `"true"` in the grid until a refresh.
     */
    patch: Partial<PersistedItem>;
}

interface UseItemsListChangesQueuesOptions {
    /**
     * Ref exposed by `useEditingRow` that stays `true` while the user is
     * actively editing a cell. Mirrors legacy `rowEditingActive` -> the
     * store queue defers its POST /items call while this is true and only
     * saves once the cell blurs and `flushStore()` is invoked.
     */
    isEditingActiveRef: React.MutableRefObject<boolean>;
    /**
     * Called after POST /validate resolves. Callers use this to update the
     * per-cell validations map + push attribute-state changes into
     * `useAttributesStateStore`.
     */
    onValidateSuccess?: (args: ValidateSuccessArgs) => void;
    /**
     * Called after POST /validate rejects. Callers can toast / log.
     */
    onValidateError?: (error: unknown, primaryKey: string) => void;
    /**
     * Called after POST /items resolves. Callers patch the React Query cache.
     */
    onSaveSuccess?: (args: SaveSuccessArgs) => void;
    /**
     * Called after POST /items rejects. Callers can toast / refetch.
     */
    onSaveError?: (error: unknown, primaryKey: string) => void;
}

/**
 * Bundles the three legacy `ItemChangesQueue` instances used by the browse
 * grid (items-controller.js 782-845):
 *
 *  - transmitItemQueue (300 ms) -> ChannelService.broadcast(ITEM_EDITED_EVENT)
 *  - validateItemQueue (600 ms) -> POST /validate
 *  - storeItemQueue    (2000 ms) -> POST /items,
 *                                  skipped while a cell edit is active,
 *                                  force-flushed on cell blur
 *
 * Exposes a per-row lifecycle (`startForRow` / `stopAll`) so the caller
 * can point all three queues at the currently edited row exactly like the
 * legacy `$scope.rowEditItem` model.
 */
function useItemsListChangesQueues({
    isEditingActiveRef,
    onValidateSuccess,
    onValidateError,
    onSaveSuccess,
    onSaveError,
}: UseItemsListChangesQueuesOptions) {
    const { user } = useUser();
    const { toast } = useToast();
    const { t } = useTranslation();

    const primaryKeyRef = useRef<string | null>(null);

    const { mutateAsync: doValidationRequest } = useRemoteValidation();
    const { mutateAsync: doSaveRequest } = useCreateOrUpdateItem();

    // Legacy processTransmitItem: broadcasts the patch of changes so that
    // other users editing the same item pick them up in real-time.
    const transmitQueue = useItemChangesQueue<PersistedItem>({
        defaultDelayMs: TRANSMIT_DEBOUNCE_MS,
        processor: async ({ patch }) => {
            const primaryKey = primaryKeyRef.current;
            if (!primaryKey) return;

            try {
                const injector = getInjector();
                const channelService = injector.get('ChannelService');
                channelService.broadcast(
                    RealTimeMessagingEvents.ITEM_EDITED_EVENT,
                    primaryKey,
                    {
                        action: RealTimeMessagingActions.ITEM_EDITED,
                        item: patch,
                        id: user?.id,
                        userId: user?.userId,
                    },
                );
            } catch (err) {
                logger.warn(
                    'useItemsListChangesQueues: transmit broadcast failed',
                    err,
                );
            }
        },
    });

    // Legacy processValidateItem: fires POST /validate with the patch;
    // response feeds the cell-level `has-error` state and attributeStates.
    const validateQueue = useItemChangesQueue<PersistedItem>({
        defaultDelayMs: VALIDATE_DEBOUNCE_MS,
        processor: async ({ patch, processedItem, processingItem }) => {
            const primaryKey = primaryKeyRef.current;
            if (!primaryKey) return;

            const attributeNames = Object.keys(patch);
            // If the row's category changes we can't do a targeted validation
            // (attributeNames=null tells the backend to re-check everything),
            // mirroring the legacy behaviour in useValidations.
            const finalAttributeNames = attributeNames.includes('category__')
                ? null
                : attributeNames;

            try {
                const response = await doValidationRequest({
                    attributeNames: finalAttributeNames,
                    newItem: sanitizeItemForSave(processingItem),
                    oldItem: sanitizeItemForSave(processedItem),
                    attributeStatesOnly: false,
                });
                onValidateSuccess?.({
                    primaryKey,
                    response,
                    attributeNames: finalAttributeNames,
                    processingItem,
                });
            } catch (err) {
                logger.error(
                    'useItemsListChangesQueues: validation request failed',
                    err,
                );
                onValidateError?.(err, primaryKey);
            }
        },
    });

    // Legacy processSaveItem: POST /items. `shouldSkip` reads
    // `isEditingActiveRef` so scheduled saves are held off while the user
    // is typing, exactly like `skipSaveItem` in items-controller.js.
    const storeQueue = useItemChangesQueue<PersistedItem>({
        defaultDelayMs: STORE_DEBOUNCE_MS,
        shouldSkip: () => Boolean(isEditingActiveRef.current),
        processor: async ({ patch, processingItem }) => {
            const primaryKey = primaryKeyRef.current;
            if (!primaryKey) return;

            try {
                const savedItem = await doSaveRequest({
                    item: processingItem,
                });
                toast.success(t('SAVE_SUCCESS_MESSAGE'));
                onSaveSuccess?.({ savedItem, patch });
            } catch (err) {
                logger.error(
                    'useItemsListChangesQueues: save request failed',
                    err,
                );
                onSaveError?.(err, primaryKey);
            }
        },
    });

    /**
     * Point all three queues at a specific row. If another row was already
     * being edited its queues are stopped without flushing (matches the
     * legacy `stopItemChangesQueues()` call inside `laxGridStartCellEdit`).
     */
    const startForRow = useCallback(
        (item: PersistedItem) => {
            transmitQueue.stop();
            validateQueue.stop();
            storeQueue.stop();

            primaryKeyRef.current = item.primaryKey__;

            transmitQueue.start(item);
            validateQueue.start(item);
            storeQueue.start(item);
        },
        [transmitQueue, validateQueue, storeQueue],
    );

    /** Stop all three queues and forget the currently edited row. */
    const stopAll = useCallback(() => {
        transmitQueue.stop();
        validateQueue.stop();
        storeQueue.stop();
        primaryKeyRef.current = null;
    }, [transmitQueue, validateQueue, storeQueue]);

    /** Fan a patch out to all three queues. Called from the RHF watch. */
    const enqueue = useCallback(
        (patch: Partial<PersistedItem>) => {
            transmitQueue.enqueueChange(patch);
            validateQueue.enqueueChange(patch);
            storeQueue.enqueueChange(patch);
        },
        [transmitQueue, validateQueue, storeQueue],
    );

    /**
     * Force the store queue to drain now. Callers must set
     * `isEditingActiveRef.current = false` BEFORE calling this - `flush()`
     * still respects `shouldSkip`, exactly like the legacy blur handler
     * that clears `rowEditingActive` before invoking
     * `storeItemQueue.processItemChanges()`.
     */
    const flushStore = useCallback(() => {
        storeQueue.flush();
    }, [storeQueue]);

    return {
        startForRow,
        stopAll,
        enqueue,
        flushStore,
        isSaving: storeQueue.isProcessing,
        isValidating: validateQueue.isProcessing,
        getCurrentPrimaryKey: () => primaryKeyRef.current,
    };
}

export { useItemsListChangesQueues };
export type { SaveSuccessArgs, ValidateSuccessArgs };
